import React, { useEffect } from 'react';
import cn from 'classnames';
import type { Data, HeaderSteps, LocalsType } from 'types';
import { FC } from 'types';

import { useInnerWidth } from 'hooks';
import { useAppDispatch } from 'hooks/useRedux';
import Logo from 'layouts/header/Logo';
import { logActivity } from 'services';
import { trackUserPageViewActivities } from 'services/userService';
import { updateAnonymousUser } from 'store/user/actions';
import { logEventGTM } from 'utils/analyticsGTM';

import headerCss from '../index.module.css';
import css from './headerWithSteps.module.css';

/* ------------------------------------------------------------------------------------ */

const MOBILE = 768;

interface HeaderWithStepsProps {
  steps: HeaderSteps['steps'];
  currentStep: string;
  locals?: LocalsType;
}

/* ------------------------------------------------------------------------------------ */

const HeaderWithSteps: FC<HeaderWithStepsProps & Data> = (props) => {
  const { steps, currentStep } = props;

  const innerWidth = useInnerWidth();
  const dispatch = useAppDispatch();
  const isCurrentStep = (step) => step.value === currentStep;

  useEffect(() => {
    const { criteria, locals } = props;

    const logPayload = {
      pageCategory: window.pageCategory,
    };

    if (criteria?.title) {
      logPayload['jobTitle'] = criteria?.title;
    }

    logActivity(logPayload)
      .then((userResponse) => {
        const user = userResponse?.data?.user;
        dispatch(updateAnonymousUser(user));

        if (!window.userId) {
          window.userId = user.userId;
        }
        window.isLoggedIn = user.isLoggedIn || false;

        logEventGTM();
      })
      .catch(() => {
        console.error('Error ocurred while logging activity inside the Header.')
      })
      .finally(() => {
        if (locals?.criteria) {
          trackUserPageViewActivities(locals.criteria);
        }
      });
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  return (
    <nav className={cn('navbar p-0', headerCss.navbar)}>
      <div className={cn('container d-flex flex-nowrap gg-16', headerCss.container)}>
        <Logo noLinks userLoggedIn={true} />

        {steps && (
          <ol className={cn('d-flex align-items-center gg-6 my-0 mx-auto p-0 w-100 overflow-hidden w-max', css.list)}>
            {steps.map((step, idx) => (
              <React.Fragment key={`${step.label}-${idx}`}>
                <li
                  className={cn('d-flex align-items-center gg-6', css.step, {
                    'white-text': isCurrentStep(step),
                  })}
                >
                  <span
                    className={cn('d-flex justify-content-center rounded-circle', css.stepNumber, {
                      [css.currentStepNumber]: isCurrentStep(step),
                    })}
                  >
                    {idx + 1}
                  </span>

                  {innerWidth > MOBILE && <span>{step.label}</span>}
                </li>

                {idx < steps.length - 1 && <span className={cn(css.separator, 'flex-fill')} />}
              </React.Fragment>
            ))}
          </ol>
        )}
      </div>
    </nav>
  );
};

export default HeaderWithSteps;
